<script lang="ts" setup>
    import { computed, inject, reactive, ref } from 'vue'
    import { useI18n } from 'vue-i18n'
    import type { UIChatMessage } from 'models'
    import PkStreamingMarkdown from './PkStreamingMarkdown.vue'
    import PkStreamingMarkdownAutoscroll from './PkStreamingMarkdownAutoscroll.vue'
    import PkChatbotStepToolDetail from './PkChatbotStepToolDetail.vue'
    import PkChatbotStepPayload from './PkChatbotStepPayload.vue'
    import {
        isReasoningPart,
        isToolPart,
        getHostCallPart,
        getPartIcon,
        getPartState,
        getToolPartLabel,
        getToolPartSummary,
        HOST_STEP_TITLES,
        hasRichToolDetail,
        hasPayload,
        formatDuration as formatDurationWith,
        LIVE_REASONING_TAIL_LENGTH,
    } from './utils'

    const props = defineProps<{
        message: UIChatMessage
        isStreaming?: boolean
        // Something is drawn below the timeline (a text, a card, a tool).
        hasContent?: boolean
        // The agent hides the text of its reasoning: its step stays, closed
        hideReasoning?: boolean
        isDark?: boolean
    }>()

    const { t: $t, n: $n } = useI18n({
        useScope: 'global',
    })
    // What each page step acts on, while the page is open
    const hostStepTitles = inject(HOST_STEP_TITLES, undefined)

    type MessagePart = UIChatMessage['parts'][number]

    interface Step {
        key: string
        part: MessagePart
        icon?: string
        label?: string
        summary?: string
        durationMs?: number
        isActive: boolean
        text?: string
        error?: string
        hasRichDetail: boolean
        hasBody: boolean
        toolInput?: unknown
        toolOutput?: unknown
    }

    const getStepError = (part: MessagePart) =>
        getPartState(part) === 'output-error' &&
        'errorText' in part &&
        typeof part.errorText === 'string'
            ? part.errorText
            : undefined

    // What the step header needs is decided here, once per recomputation.
    // `hasBody` was a function the template called six times per step, and
    // `steps` recomputes at every delta of the stream: on a tool answering
    // hundreds of kilobytes, where the answer comes from `hasPayload` and
    // `hasPayload` serializes the whole payload to find out, that is the same
    // JSON.stringify six times for one boolean.
    //
    // The two payload blocks keep asking for themselves, because they render
    // only inside an expanded step: deciding here whether to show the output
    // would serialize it for every step the reader never opens, which is all
    // of them by default.
    const steps = computed<Step[]>(() => {
        const parts = props.message.parts
        const partTimings = props.message.metadata?.partTimings
        const result: Step[] = []
        for (const [index, part] of parts.entries()) {
            const isReasoning = isReasoningPart(part)
            const host = getHostCallPart(part)
            const isTool = isToolPart(part) || Boolean(host)
            if (!isReasoning && !isTool) {
                continue
            }
            // A host call stays open while the page runs it, after the stream
            // that announced it has ended: every open one is in progress then,
            // and one still open once nothing runs got no answer.
            const hostOpen = host?.state === 'input-available'
            const toolCallId =
                'toolCallId' in part ? (part.toolCallId as string) : undefined
            const text =
                isReasoning && !props.hideReasoning && part.text.trim()
                    ? part.text
                    : undefined
            const error = getStepError(part)
            const hasRichDetail = hasRichToolDetail(part)
            const toolInput = isTool && 'input' in part ? part.input : undefined
            const toolOutput =
                isTool && 'output' in part ? part.output : undefined
            result.push({
                key: toolCallId ?? `${part.type}-${index}`,
                part,
                icon: getPartIcon(part),
                label: getToolPartLabel($t, part, hostStepTitles?.value),
                summary:
                    hostOpen && !props.isStreaming
                        ? $t('messagePart.hostToolNoAnswer')
                        : getToolPartSummary(part),
                durationMs: toolCallId ? partTimings?.[toolCallId] : undefined,
                isActive:
                    Boolean(props.isStreaming) &&
                    (host ? hostOpen : index === parts.length - 1),
                text,
                error,
                hasRichDetail,
                // The `||` short-circuits as the old function did: a tool
                // with an input never serializes its output to answer this.
                hasBody: Boolean(
                    text ||
                    error ||
                    (hasRichDetail
                        ? toolInput !== undefined || toolOutput !== undefined
                        : hasPayload(toolInput) || hasPayload(toolOutput)),
                ),
                toolInput,
                toolOutput,
            })
        }
        return result
    })

    const totalDurationMs = computed(() => {
        const { createdAt, completedAt } = props.message.metadata ?? {}
        if (!createdAt || !completedAt) {
            return undefined
        }
        return completedAt - createdAt
    })

    const formatDuration = (ms?: number) => formatDurationWith($n, ms)

    // --- Expand / collapse ---
    // Every step starts closed, the running one included: the timeline says
    // what the answer is doing, the body is opened only when asked for. The
    // reasoning of the active step still follows the stream once opened.
    const manualSteps = reactive(new Map<string, boolean>())
    const isStepExpanded = (step: Step) => manualSteps.get(step.key) ?? false
    const toggleStep = (step: Step) => {
        manualSteps.set(step.key, !isStepExpanded(step))
    }

    // The group is open while streaming and collapses to its header as soon
    // as the answer draws something below it, unless the user toggled it
    // manually: from then on the activity reads at the bottom of the answer,
    // and an open list growing above it would push the answer down.
    //
    // The user's choices last as long as the message: an answer that resumes
    // after a pause (an approval, a form, a tool the client runs) streams
    // into the same message and must not close what the reader opened. A
    // regenerated answer is a new message, with a fresh timeline.
    const manualGroup = ref<boolean | undefined>(undefined)
    const isGroupExpanded = computed(
        () =>
            manualGroup.value ??
            (Boolean(props.isStreaming) && !props.hasContent),
    )
</script>

<template>
    <div v-if="steps.length" class="pk-chatbot-steps">
        <button
            type="button"
            class="pk-chatbot-steps__header"
            :aria-expanded="isGroupExpanded"
            @click="manualGroup = !isGroupExpanded">
            <VvIcon v-if="isStreaming" name="loading-loop" class="shrink-0" />
            <VvIcon v-else name="ri:route-line" class="shrink-0" />
            <span class="pk-chatbot-steps__title">
                {{ $t('messagePart.steps') }}
            </span>
            <span class="pk-chatbot-steps__meta">
                {{ $t('messagePart.stepCount', steps.length) }}
                <template v-if="formatDuration(totalDurationMs)">
                    · {{ formatDuration(totalDurationMs) }}
                </template>
            </span>
            <VvIcon
                :name="
                    isGroupExpanded
                        ? 'ri:arrow-up-s-line'
                        : 'ri:arrow-down-s-line'
                "
                class="pk-chatbot-steps__chevron shrink-0 ml-auto" />
        </button>
        <ol v-if="isGroupExpanded" class="pk-chatbot-steps__list">
            <li
                v-for="step in steps"
                :key="step.key"
                class="pk-chatbot-steps__step"
                :class="{
                    'pk-chatbot-steps__step--active': step.isActive,
                }">
                <component
                    :is="step.hasBody ? 'button' : 'div'"
                    :type="step.hasBody ? 'button' : undefined"
                    class="pk-chatbot-steps__step-header"
                    :aria-expanded="
                        step.hasBody ? isStepExpanded(step) : undefined
                    "
                    @click="step.hasBody ? toggleStep(step) : undefined">
                    <span
                        class="pk-chatbot-steps-marker"
                        :class="{
                            'pk-chatbot-steps-marker--active': step.isActive,
                        }">
                        <VvIcon v-if="step.isActive" name="loading-loop" />
                        <VvIcon v-else-if="step.icon" :name="step.icon" />
                    </span>
                    <span class="pk-chatbot-steps__step-label">
                        {{ step.label }}
                    </span>
                    <span class="pk-chatbot-steps__step-summary">
                        {{ step.summary }}
                    </span>
                    <span
                        v-if="formatDuration(step.durationMs)"
                        class="pk-chatbot-steps__step-duration">
                        {{ formatDuration(step.durationMs) }}
                    </span>
                    <VvIcon
                        v-if="step.hasBody"
                        :name="
                            isStepExpanded(step)
                                ? 'ri:arrow-up-s-line'
                                : 'ri:arrow-down-s-line'
                        "
                        class="pk-chatbot-steps__chevron shrink-0 ml-auto" />
                </component>
                <div
                    v-if="step.hasBody && isStepExpanded(step)"
                    class="pk-chatbot-steps__step-body">
                    <PkStreamingMarkdownAutoscroll
                        v-if="step.text && step.isActive"
                        :markdown="step.text"
                        :is-dark
                        :tail-length="LIVE_REASONING_TAIL_LENGTH"
                        inner-class="wysiwyg"
                        class="pk-chatbot-steps__step-content pk-chatbot-steps__step-content--scroll" />
                    <div
                        v-else-if="step.text"
                        class="pk-chatbot-steps__step-content pk-chatbot-steps__step-content--scroll light-scrollbar">
                        <PkStreamingMarkdown
                            :markdown="step.text"
                            :is-dark
                            class="wysiwyg" />
                    </div>
                    <div
                        v-else-if="step.error"
                        class="pk-chatbot-steps__step-payload pk-chatbot-steps__step-payload--error">
                        <span class="pk-chatbot-steps__step-payload-label">
                            {{ $t('messagePart.stepError') }}
                        </span>
                        <pre>{{ step.error }}</pre>
                    </div>
                    <PkChatbotStepToolDetail
                        v-else-if="step.hasRichDetail"
                        :part="step.part" />
                    <template v-else>
                        <PkChatbotStepPayload
                            v-if="hasPayload(step.toolInput)"
                            :label="$t('messagePart.stepInput')"
                            :value="step.toolInput" />
                        <PkChatbotStepPayload
                            v-if="hasPayload(step.toolOutput)"
                            :label="$t('messagePart.stepOutput')"
                            :value="step.toolOutput" />
                    </template>
                </div>
            </li>
        </ol>
    </div>
</template>

<style lang="scss">
    .pk-chatbot-steps {
        width: 100%;
        border: var(--spacing-px) solid var(--color-surface-3);
        border-radius: var(--rounded-lg);
        background-color: var(--color-surface-1);
        font-size: var(--text-12);
        color: var(--color-word-3);
        overflow: hidden;

        &__header {
            display: flex;
            align-items: center;
            gap: var(--spacing-8);
            width: 100%;
            min-height: var(--spacing-40);
            padding: var(--spacing-8) var(--spacing-sm);
            text-align: left;
            cursor: pointer;
            transition: background-color 0.2s ease;

            &:hover {
                background-color: var(--color-surface-2);
            }
        }

        &__chevron {
            font-size: var(--text-16);
        }

        &__title {
            font-weight: 600;
            color: var(--color-word-2);
            flex-grow: 1;
        }

        &__meta {
            color: var(--color-word-4);
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
        }

        &__list {
            background-color: var(--color-surface);
            border-top: var(--spacing-px) solid var(--color-surface-3);
            padding: var(--spacing-8) var(--spacing-sm);
            display: flex;
            flex-direction: column;
        }

        &__step {
            position: relative;

            // Vertical connector between step markers
            &:not(:last-child)::before {
                content: '';
                position: absolute;
                left: var(--spacing-10);
                top: var(--spacing-28);
                bottom: calc(var(--spacing-8) * -1);
                width: var(--spacing-px);
                background-color: var(--color-surface-4);
            }
        }

        &__step-header {
            display: flex;
            align-items: center;
            gap: var(--spacing-8);
            width: calc(100% + var(--spacing-16));
            min-height: var(--spacing-36);
            margin-inline: calc(var(--spacing-8) * -1);
            padding: var(--spacing-4) var(--spacing-8);
            text-align: left;

            &:is(button) {
                cursor: pointer;
                border-radius: var(--rounded);
                transition: background-color 0.2s ease;

                &:hover {
                    background-color: var(--color-surface-2);
                }
            }
        }

        &-marker {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: var(--spacing-20);
            height: var(--spacing-20);
            flex-shrink: 0;
            position: relative;
            z-index: 1;
            background-color: var(--color-surface-1);
            border-radius: var(--rounded-full);
            border: var(--spacing-px) solid var(--color-surface-4);

            &--active {
                background-color: var(
                    --chatbot-main-color,
                    var(--color-surface-4)
                );
                border-color: transparent;
                color: var(--chatbot-contrast-color, var(--color-word-1));
            }
        }

        &__step-label {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: var(--color-word-2);
        }

        &__step-summary {
            flex: 1 1 0;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: var(--color-word-4);
        }

        &__step-duration {
            color: var(--color-word-4);
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
        }

        &__step--active &__step-label {
            color: var(--color-word-1);
        }

        &__step-body {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-4);
            margin-left: var(--spacing-28);
            margin-block: var(--spacing-4) var(--spacing-8);
        }

        &__step-content {
            border: var(--spacing-px) solid var(--color-surface-3);
            border-radius: var(--rounded);
            padding: var(--spacing-8);
            background-color: var(--color-surface-1);

            // The finished reasoning keeps the cap the streaming one had:
            // `.wysiwyg` hides its own overflow, so the scroll belongs to the
            // wrapper, exactly as in the autoscroll variant.
            &--scroll {
                max-height: var(--spacing-256);
                overflow: auto;
            }
        }

        &__step-payload {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-2);

            pre {
                border: var(--spacing-px) solid var(--color-surface-3);
                border-radius: var(--rounded);
                padding: var(--spacing-8);
                font-size: var(--text-10);
                max-height: var(--spacing-160);
                overflow: auto;
                white-space: pre-wrap;
                overflow-wrap: anywhere;
                font-family: var(--font-mono);
                background-color: var(--color-surface-1);
            }
        }

        &__step-payload-label {
            font-weight: 600;
            color: var(--color-word-3);
            text-transform: uppercase;
            font-size: var(--text-10);
            letter-spacing: 0.05em;
        }

        &__step-payload--error {
            .pk-chatbot-steps__step-payload-label,
            pre {
                color: var(--u-danger-text, var(--color-danger));
            }

            pre {
                border-color: var(--color-danger-lighten-5);
            }
        }
    }
</style>
